<template>
  <h5 class="text-[19px]/[22px] sm:text-[27px]/[31px] font-semibold mt-[17px] mb-[13px]">
    Your information
  </h5>
  <div class="flex flex-col gap-[17px]">
    <div class="w-full flex flex-col justify-center relative">
      <CustomField
          :name="`customer_name`"
          :rules="[required]"
          :placeholder="`Full name`"
      />
      <span class="text-red-500 text-xs pt-1">{{ errors[`customer_name`] }}</span>
    </div>
    <div class="w-full flex flex-col justify-center relative">
      <CustomField
          :name="`customer_email`"
          :rules="[emailValidationWithEmpty]"
          :placeholder="`Email`"
          @blur="handleEmailBlur"
      />
      <span class="text-red-500 text-xs pt-1">{{ errors[`customer_email`] }}</span>
    </div>
    <div class="w-full flex flex-col justify-center relative" v-if="emailEmptyShowChecker">
      <Field
          name="customer_empty_email"
          type="checkbox"
          :value="true"
          :unchecked-value="false"
          v-slot="{ field }"
      >
        <label class="flex items-center gap-[10px]">
          <input
              v-bind="field"
              type="checkbox"
              class="w-[25px] h-[25px] rounded-small border border-charcoal cursor-pointer accent-vivid-purple"
          />
          I don't have email
        </label>
      </Field>
      <span class="text-red-500 text-xs pt-1">{{ errors[`customer_empty_email`] }}</span>
    </div>
    <div class="w-full flex flex-col justify-center relative">
	      <CustomField name="customer_phone" :rules="[required]" placeholder="Phone" />
	      <span class="text-red-500 text-xs pt-1">{{ errors[`customer_phone`] }}</span>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, watch } from 'vue'
import CustomField from '@components/reusable/CustomField.vue'
import { required, emailValidationWithEmpty, emailValidation } from '@utills/helpers/validation'
import { Field, useFieldValue } from 'vee-validate'
import { useDebounceFn } from '@vueuse/core'
export default defineComponent({
  components: {Field, CustomField },
  props: {
    emailEmptyShowChecker: {
      type: Boolean,
      required: true,
      default:false
    },
    errors: {
      type: Object,
      required: true
    },
    onEmailBlur: {
      type: Function,
      required: false,
      default: null
    }
  },
  setup(props) {
	    const emailValue = useFieldValue('customer_email')
	    const phoneValue = useFieldValue('customer_phone')

	    const sendCustomerUpdate = async () => {
	      if (!props.onEmailBlur) return

	      const emailRaw = emailValue.value as string | undefined
	      const phoneRaw = phoneValue.value as string | undefined
		      
		      // New behavior: do not attempt to apply the new-customer discount
		      // (or run eligibility checks) until we have at least a phone number.
		      // This prevents early discount application when only an email exists.
		      if (!phoneRaw) {
		        return
		      }

	      const payload: { email?: string; phone?: string } = {}

	      if (emailRaw) {
	        const validationResult = emailValidation(emailRaw)
	        if (validationResult === true) {
	          payload.email = emailRaw
	        }
	      }

	      if (phoneRaw) {
	        payload.phone = phoneRaw
	      }

	      // Don't call the API if we have neither email nor phone
	      if (!payload.email && !payload.phone) return

	      try {
	        await props.onEmailBlur(payload)
	      } catch (error) {
	        console.error('Error setting customer contact:', error)
	      }
	    }

	    const handleEmailBlur = async () => {
	      // When email loses focus, send the latest valid contact details immediately.
	      await sendCustomerUpdate()
	    }

	    const debouncedSendCustomerUpdate = useDebounceFn(sendCustomerUpdate, 500)

	    // Watch for changes to the phone number and update customer/discount info
	    watch(phoneValue, () => {
	      debouncedSendCustomerUpdate()
	    })

    return {
      required,
      emailValidationWithEmpty,
      handleEmailBlur
    }
  }
})
</script>